import React, { useCallback, useEffect, useState } from "react";
import { client } from "../Clients";
import type {
	BackupJobDto,
	BackupDestinationDto,
	BackupInstanceDto,
	BackupMetricsDto,
	BackupArchiveDto
} from "./Backup/backupClient";

/**
 * Multi-instance aware backup admin page. Polls the REST API every 2 s and renders:
 * - Overview metrics (last full, degraded/failed counts)
 * - Instances card (per-API-process heartbeat table, with current-instance highlight)
 * - Destinations with health pills and primary flag
 * - Jobs table with fan-out degraded badges
 *
 * Backed by the CMS.Core.Backup.AspNetCore controller. SignalR push will
 * replace the polling loop in a later drop without changing this component's shape.
 */
const Backup: React.FC = () => {
	const [jobs, setJobs] = useState<BackupJobDto[]>([]);
	const [destinations, setDestinations] = useState<BackupDestinationDto[]>([]);
	const [instances, setInstances] = useState<BackupInstanceDto[]>([]);
	const [metrics, setMetrics] = useState<BackupMetricsDto | null>(null);
	const [archives, setArchives] = useState<BackupArchiveDto[]>([]);
	const [creating, setCreating] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// Archive actions: which archive has its delete pending confirmation / which is busy.
	const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
	const [busyArchiveId, setBusyArchiveId] = useState<string | null>(null);
	const [actionMessage, setActionMessage] = useState<string | null>(null);

	const refresh = useCallback(async () => {
		try {
			const [j, d, i, m, a] = await Promise.all([
				client.backup.listJobs({ take: 50 }),
				client.backup.listDestinations(),
				client.backup.listInstances(),
				client.backup.getMetrics(),
				client.backup.listArchives()
			]);
			setJobs(j);
			setDestinations(d);
			setInstances(i);
			setMetrics(m);
			setArchives(a);
			setError(null);
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		}
	}, []);

	useEffect(() => {
		refresh();
		const id = setInterval(refresh, 2000);
		return () => clearInterval(id);
	}, [refresh]);

	const createJob = useCallback(async (kind: string) => {
		setCreating(true);
		try {
			await client.backup.createJob({ kind, scope: "both" });
			await refresh();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setCreating(false);
		}
	}, [refresh]);

	// Permanently removes one archive from the destination. Two-step in the UI
	// (Supprimer → Confirmer) and the API additionally demands the destructive
	// confirmation header, which the client sends.
	const deleteArchive = useCallback(async (archive: BackupArchiveDto) => {
		setBusyArchiveId(archive.id);
		setActionMessage(null);
		try {
			const result = await client.backup.deleteArchive(archive.id, archive.destinationId || undefined);
			setActionMessage(`Archive ${result.id} supprimée (${result.filesDeleted} fichiers).`);
			await refresh();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusyArchiveId(null);
			setConfirmDeleteId(null);
		}
	}, [refresh]);

	// Merge-mode restore: re-applies the archive's rows over the current database
	// (no deletes). Replace mode stays a deliberate API/CLI operation.
	const restoreArchive = useCallback(async (archive: BackupArchiveDto) => {
		setBusyArchiveId(archive.id);
		setActionMessage(null);
		try {
			const result = await client.backup.restore({
				backupId: archive.id,
				destinationId: archive.destinationId,
				mode: "merge"
			});
			const totals = result.tableDeltas.reduce(
				(acc, d) => ({ inserts: acc.inserts + d.inserts, updates: acc.updates + d.updates }),
				{ inserts: 0, updates: 0 }
			);
			setActionMessage(
				`Restauration de ${result.backupId} terminée en ${Math.round(result.durationMs / 1000)}s — ` +
				`${totals.inserts} insertions, ${totals.updates} mises à jour (mode merge).`
			);
			await refresh();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusyArchiveId(null);
		}
	}, [refresh]);

	const applyRetention = useCallback(async () => {
		setActionMessage(null);
		try {
			const result = await client.backup.applyRetention();
			setActionMessage(
				result.deleted.length === 0
					? "Rétention appliquée — aucune archive à expirer."
					: `Rétention appliquée — ${result.deleted.length} archive(s) expirée(s), ${result.kept.length} conservée(s).`
			);
			await refresh();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		}
	}, [refresh]);

	const lastFullAge = metrics?.lastSuccessfulFullAgeSeconds;
	const lastFullLabel = lastFullAge == null
		? "never"
		: lastFullAge < 3600 ? `${Math.floor(lastFullAge / 60)} min ago`
		: lastFullAge < 86400 ? `${Math.floor(lastFullAge / 3600)} h ago`
		: `${Math.floor(lastFullAge / 86400)} d ago`;

	return (
		<div style={{ padding: 24, fontFamily: "system-ui, sans-serif" }}>
			<header style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
				<h1 style={{ margin: 0 }}>Backups</h1>
				<div>
					<button disabled={creating} onClick={() => createJob("full")}>Run full backup</button>
					<button disabled={creating} onClick={() => createJob("incremental")} style={{ marginLeft: 8 }}>
						Run incremental
					</button>
				</div>
			</header>

			{error && (
				<div style={{ color: "#b00020", padding: 12, border: "1px solid #b00020", marginTop: 16, borderRadius: 4 }}>
					{error}
				</div>
			)}

			<section style={{ marginTop: 24 }}>
				<h2 style={{ fontSize: 18 }}>Overview</h2>
				<div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
					<Card title="Last successful full" value={lastFullLabel} />
					<Card title="Recent jobs" value={String(metrics?.recentJobs ?? 0)} />
					<Card title="Degraded jobs" value={String(metrics?.degradedJobs ?? 0)} tone={metrics && metrics.degradedJobs > 0 ? "warn" : undefined} />
					<Card title="Failed jobs" value={String(metrics?.failedJobs ?? 0)} tone={metrics && metrics.failedJobs > 0 ? "error" : undefined} />
				</div>
			</section>

			<section style={{ marginTop: 24 }}>
				<h2 style={{ fontSize: 18 }}>Instances ({instances.length})</h2>
				<Table
					headers={["Instance", "Running jobs", "GC lock", "Last heartbeat"]}
					rows={instances.map((inst) => ({
						key: inst.instanceId,
						cells: [
							<span>
								<code>{inst.instanceId}</code>
								{inst.isCurrent && <span style={{ marginLeft: 8, fontSize: 11, color: "#2e7d32" }}>(this instance)</span>}
							</span>,
							String(inst.runningJobs),
							inst.holdsGcLock ? "🔒 holding" : "—",
							new Date(inst.lastSeenUtc).toLocaleString()
						]
					}))}
					emptyLabel="No instances checked in within the last 30 minutes."
				/>
			</section>

			<section style={{ marginTop: 24 }}>
				<h2 style={{ fontSize: 18 }}>Destinations ({destinations.length})</h2>
				<Table
					headers={["Id", "Kind", "Encryption", "Flags", "Health"]}
					rows={destinations.map((d) => ({
						key: d.id,
						cells: [
							d.id,
							d.kind,
							d.encryption,
							[d.primary ? "primary" : null, d.enabled ? null : "disabled"].filter(Boolean).join(", ") || "—",
							<HealthPill value={d.health} />
						]
					}))}
					emptyLabel="No destinations configured."
				/>
			</section>

			<section style={{ marginTop: 24 }}>
				<div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
					<h2 style={{ fontSize: 18 }}>Archives ({archives.length})</h2>
					<button onClick={applyRetention} title="Expire les archives hors de la politique de rétention (GFS)">
						Appliquer la rétention
					</button>
				</div>
				{actionMessage && (
					<div style={{ color: "#2e7d32", padding: 10, border: "1px solid #2e7d32", marginTop: 8, borderRadius: 4, fontSize: 13 }}>
						{actionMessage}
					</div>
				)}
				<Table
					headers={["Id", "Kind", "Date", "Rows", "Size", "Encryption", "Actions"]}
					rows={archives.map((a) => ({
						key: a.id,
						cells: [
							<code style={{ fontSize: 11 }}>{a.id}</code>,
							a.kind,
							new Date(a.startedUtc).toLocaleString(),
							String(a.rowCount),
							formatBytes(a.bytesWritten),
							a.encryption,
							<span style={{ whiteSpace: "nowrap" }}>
								<button
									disabled={busyArchiveId !== null}
									onClick={() => restoreArchive(a)}
									title="Restaure les lignes de cette archive dans la base (mode merge — aucune suppression)"
								>
									{busyArchiveId === a.id ? "…" : "Restaurer"}
								</button>
								{confirmDeleteId === a.id ? (
									<>
										<button
											disabled={busyArchiveId !== null}
											onClick={() => deleteArchive(a)}
											style={{ marginLeft: 6, color: "white", background: "#b00020", border: "1px solid #b00020", borderRadius: 3 }}
										>
											Confirmer la suppression
										</button>
										<button disabled={busyArchiveId !== null} onClick={() => setConfirmDeleteId(null)} style={{ marginLeft: 4 }}>
											Annuler
										</button>
									</>
								) : (
									<button
										disabled={busyArchiveId !== null}
										onClick={() => setConfirmDeleteId(a.id)}
										style={{ marginLeft: 6, color: "#b00020" }}
										title="Supprime définitivement cette archive de la destination"
									>
										Supprimer
									</button>
								)}
							</span>
						]
					}))}
					emptyLabel="No archives on the destination yet — run a backup above."
				/>
			</section>

			<section style={{ marginTop: 24 }}>
				<h2 style={{ fontSize: 18 }}>Recent jobs ({jobs.length})</h2>
				<Table
					headers={["Id", "Kind", "Status", "Destination", "Instance", "Rows", "Bytes", "Started"]}
					rows={jobs.map((j) => ({
						key: j.id,
						cells: [
							<code style={{ fontSize: 11 }}>{j.id}</code>,
							j.kind,
							<StatusBadge status={j.status} degraded={j.isDegraded} />,
							j.destinationId,
							j.instanceId ?? "—",
							String(j.rowsExported),
							formatBytes(j.bytesWritten),
							new Date(j.startedAt).toLocaleString()
						]
					}))}
					emptyLabel="No backup jobs yet. Click 'Run full backup' above to create one."
				/>
			</section>
		</div>
	);
};

const Card: React.FC<{ title: string; value: string; tone?: "warn" | "error" }> = ({ title, value, tone }) => (
	<div
		style={{
			border: "1px solid #ddd",
			borderLeft: `4px solid ${tone === "error" ? "#b00020" : tone === "warn" ? "#f9a825" : "#2e7d32"}`,
			padding: 12,
			borderRadius: 4,
			background: "#fafafa"
		}}
	>
		<div style={{ fontSize: 12, color: "#666" }}>{title}</div>
		<div style={{ fontSize: 20, fontWeight: 600 }}>{value}</div>
	</div>
);

const StatusBadge: React.FC<{ status: string; degraded: boolean }> = ({ status, degraded }) => {
	const color = status === "Completed" && !degraded ? "#2e7d32"
		: status === "Completed" && degraded ? "#f9a825"
		: status === "Failed" ? "#b00020"
		: status === "Running" ? "#1565c0"
		: "#666";
	return (
		<span style={{ color, fontWeight: 600 }}>
			{status}{degraded && " (Degraded)"}
		</span>
	);
};

const HealthPill: React.FC<{ value: string }> = ({ value }) => {
	const color = value === "healthy" ? "#2e7d32"
		: value === "degraded" ? "#f9a825"
		: value === "unhealthy" ? "#b00020"
		: "#999";
	return (
		<span style={{
			display: "inline-block",
			padding: "2px 8px",
			borderRadius: 12,
			background: color,
			color: "white",
			fontSize: 11
		}}>{value}</span>
	);
};

interface TableProps {
	headers: string[];
	rows: { key: string; cells: React.ReactNode[] }[];
	emptyLabel: string;
}

const Table: React.FC<TableProps> = ({ headers, rows, emptyLabel }) => {
	if (rows.length === 0) {
		return <div style={{ color: "#666", fontStyle: "italic", padding: 12 }}>{emptyLabel}</div>;
	}
	return (
		<table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
			<thead>
				<tr>
					{headers.map((h) => (
						<th key={h} style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "6px 8px", fontSize: 12, color: "#666" }}>
							{h}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.map((r) => (
					<tr key={r.key}>
						{r.cells.map((c, i) => (
							<td key={i} style={{ borderBottom: "1px solid #eee", padding: "6px 8px", fontSize: 13 }}>{c}</td>
						))}
					</tr>
				))}
			</tbody>
		</table>
	);
};

function formatBytes(bytes: number): string {
	if (bytes === 0) return "0";
	const units = ["B", "KB", "MB", "GB", "TB"];
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

export default Backup;
